import { useTranslation } from "react-i18next";
import { Card, CardTitle, Table, Td, Th } from "@/core/ui";
import { usePuzzleStats } from "./api";
import { formatDuration } from "./time";

export interface DecileBar {
  decile: number;
  ms: number;
  height: number;
}

/** Bar heights as a fraction of the slowest decile, so an empty list yields no bars. */
export function decileBars(deciles: number[]): DecileBar[] {
  const max = Math.max(...deciles, 0);
  if (!Number.isFinite(max) || max <= 0) return [];
  return deciles.map((ms, index) => ({ decile: (index + 1) * 10, ms, height: ms / max }));
}

/**
 * Public per-puzzle statistics. Nothing here can hint at the solution: only how many people
 * played, how long they took and how many tries they needed.
 */
export function PuzzleStatsPanel({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const stats = usePuzzleStats(slug);
  if (stats.isPending || !stats.data) return null;
  const { started, solved, success_rate, median_ms, deciles_ms, avg_attempts } = stats.data;
  const bars = decileBars(deciles_ms ?? []);

  return (
    <Card className="bt-card--panel">
      <CardTitle>{t("stats.title")}</CardTitle>
      {started === 0 ? (
        <p className="bt-stats__empty">{t("stats.empty")}</p>
      ) : (
        <>
          <dl className="bt-stats__figures">
            <div className="bt-stats__figure">
              <dt className="bt-stats__label">{t("stats.success_rate")}</dt>
              <dd className="bt-stats__value">
                {success_rate === null ? "–" : `${Math.round(success_rate * 100)} %`}
              </dd>
            </div>
            <div className="bt-stats__figure">
              <dt className="bt-stats__label">{t("stats.median")}</dt>
              <dd className="bt-stats__value bt-stats__value--duration">
                {median_ms === null ? "–" : formatDuration(median_ms)}
              </dd>
            </div>
            <div className="bt-stats__figure">
              <dt className="bt-stats__label">{t("stats.solved")}</dt>
              <dd className="bt-stats__value">{t("stats.solved_value", { solved, started })}</dd>
            </div>
            <div className="bt-stats__figure">
              <dt className="bt-stats__label">{t("stats.avg_attempts")}</dt>
              <dd className="bt-stats__value">{avg_attempts === null ? "–" : avg_attempts}</dd>
            </div>
          </dl>

          {bars.length > 0 ? (
            <figure>
              <figcaption className="bt-stats__caption">{t("stats.deciles")}</figcaption>
              <svg
                viewBox={`0 0 ${bars.length * 10} 32`}
                className="bt-stats__chart"
                role="img"
                aria-label={t("stats.deciles_summary", {
                  fastest: formatDuration(bars[0].ms),
                  slowest: formatDuration(bars[bars.length - 1].ms),
                })}
                preserveAspectRatio="none"
              >
                {bars.map((bar, index) => (
                  <rect
                    key={bar.decile}
                    x={index * 10 + 1}
                    y={32 - bar.height * 30}
                    width={8}
                    height={Math.max(1, bar.height * 30)}
                    className="bt-stats__bar"
                  />
                ))}
              </svg>
              {/* The same numbers, reachable without seeing the chart. */}
              <details className="bt-stats__table-toggle">
                <summary className="bt-stats__table-summary">{t("stats.deciles_table")}</summary>
                <Table className="bt-stats__table">
                  <thead>
                    <tr>
                      <Th>{t("stats.decile")}</Th>
                      <Th>{t("play.time")}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bars.map((bar) => (
                      <tr key={bar.decile}>
                        <Td>{t("stats.decile_n", { value: bar.decile })}</Td>
                        <Td className="bt-table__cell--numeric bt-table__cell--duration">
                          {formatDuration(bar.ms)}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </details>
            </figure>
          ) : null}
        </>
      )}
    </Card>
  );
}
